import React, { useEffect, useMemo, useState } from "react";
import { Table, Typography, Tag, Space, Input, message, Tooltip, Button, Modal, Form, Select } from "antd";
import AxiosOICStat from "AxiosOICStat";
import { useSelector } from 'react-redux';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import AddSdgMappingModal from "components/modals/AddSdgMappingModal";
import EditSdgMappingModal from "components/modals/EditSdgMappingModal";
import EditCountryMappingModal from "components/modals/EditCountryMappingModal";
import { getIndicatorName } from "../../utils/indicatorName";

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

export default function SdgReviewTab({ mappingType = "indicator" }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [countryQuery, setCountryQuery] = useState("");
  const indicatorsState = useSelector((state) => state.indicators);
  const countriesState = useSelector((state) => state.countries);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editForm] = Form.useForm();
  const [sdgSeries, setSdgSeries] = useState([]);
  const [dimensionKeys, setDimensionKeys] = useState([]);
  const [dimensionOptions, setDimensionOptions] = useState({});
  const [loadingDimensions, setLoadingDimensions] = useState(false);
  const [countryMappingData, setCountryMappingData] = useState([]);
  const [countryEditModalVisible, setCountryEditModalVisible] = useState(false);
  const [countryAddModalVisible, setCountryAddModalVisible] = useState(false);
  const [editingCountryRecord, setEditingCountryRecord] = useState(null);
  const [countryEditForm] = Form.useForm();
  const [countryAddForm] = Form.useForm();

  const dimensionFieldMapping = {
    "Activity": "Activity",
    "Age": "Age", 
    "Bioclimatic belt": "Bioclimatic_belt",
    "Cause of death": "Cause_of_death",
    "Cities": "Cities",
    "Counterpart": "Counterpart",
    "Custom Breakdown": "Custom_Breakdown",
    "Deviation Level": "Deviation_Level",
    "Disability status": "Disability_status",
    "Education level": "Education_level",
    "Fiscal intervention stage": "Fiscal_intervention_stage",
    "Food Waste Sector": "Food_Waste_Sector",
    "Freq": "Freq",
    "Frequency of Chlorophyll-a concentration": "Frequency_of_Chlorophyll_a_concentration",
    "Government_Name": "Government_Name",
    "Grounds of discrimination": "Grounds_of_discrimination",
    "Hazard type": "Hazard_type",
    "IHR Capacity": "IHR_Capacity",
    "Illicit Financial Flows": "Illicit_Financial_Flows",
    "Land cover": "Land_cover",
    "Level of requirement": "Level_of_requirement",
    "Level/Status": "Level_Status",
    "Level_of_government": "Level_of_government",
    "Location": "Location",
    "Marine Spatial Planning (MSP)": "Marine_Spatial_Planning_MSP",
    "Migratory status": "Migratory_status",
    "Mode of transportation": "Mode_of_transportation",
    "Mountain Elevation": "Mountain_Elevation",
    "Name of international agreement": "Name_of_international_agreement",
    "Name of international institution": "Name_of_international_institution",
    "Name of non-communicable disease": "Name_of_non_communicable_disease",
    "Nutrient Loading": "Nutrient_Loading",
    "Parliamentary committees": "Parliamentary_committees",
    "Policy Domains": "Policy_Domains",
    "Policy instruments": "Policy_instruments",
    "Population Group": "Population_Group",
    "Quantile": "Quantile",
    "Report Ordinal": "Report_Ordinal",
    "Reporting Type": "Reporting_Type",
    "Sampling Stations": "Sampling_Stations",
    "Service Attribute": "Service_Attribute",
    "Severity of price levels": "Severity_of_price_levels",
    "Sex": "Sex",
    "Substance use disorders": "Substance_use_disorders",
    "Tariff regime (status)": "Tariff_regime_status",
    "Type of facilities": "Type_of_facilities",
    "Type of mobile technology": "Type_of_mobile_technology",
    "Type of occupation": "Type_of_occupation",
    "Type of OFDI scheme": "Type_of_OFDI_scheme",
    "Type of product": "Type_of_product",
    "Type of renewable technology": "Type_of_renewable_technology",
    "Type of skill": "Type_of_skill",
    "Type of speed": "Type_of_speed",
    "Type of support": "Type_of_support",
    "Type of waste treatment": "Type_of_waste_treatment"
  };

  const handleEdit = (record) => () => {
      setEditingRecord(record);
      setEditModalVisible(true);

      const nonEmptyDimensions = dimensionKeys.filter((key) => {
        const fieldName = dimensionFieldMapping[key] || key;
        const value = record[fieldName];
        return value !== null && value !== undefined && String(value).trim() !== "";
      });

      const formValues = {
        SeriesDescription: record.SeriesDescription,
        SeriesCode: record.SeriesCode,
        ind_code: record.ind_code,
        Indicator: record.Indicator,
      };

      nonEmptyDimensions.forEach((key) => {
        const fieldName = dimensionFieldMapping[key] || key;
        formValues[fieldName] = record[fieldName];
      });

      editForm.setFieldsValue(formValues);
    };

  const fetchDimensionOptions = async () => {
    if (dimensionKeys.length === 0) return;
    
    setLoadingDimensions(true);
    try {
      const response = await AxiosOICStat.post(`/external_api/sdg/get-sdg-dimension-options`, {
        dim_names: dimensionKeys
      });

      if (response.data) {
        setDimensionOptions(response.data);
      } else {
        message.error('Failed to fetch dimension options');
      }
    } catch (error) {
      console.error('Error fetching dimension options:', error);
      message.error('Error loading dimension options');
    } finally {
      setLoadingDimensions(false);
    }
  };

  const fetchDimNames = async () => {
    try {
      const res = await AxiosOICStat.get("/external_api/sdg/get-sdg-dimension-names");
      setDimensionKeys(res.data || []);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to load SDG series. Please try again.";
      message.error(detail);
      return [];
    }
  };

  const fetchSdgSeries = async () => {
    try {
      const res = await AxiosOICStat.get("/external_api/sdg/get-all-sdg-series");
      setSdgSeries(res.data || []);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to load SDG series. Please try again.";
      message.error(detail);
      return [];
    }
  };

  const fetchData = async () => {
      setLoading(true);
      try {
        const res = await AxiosOICStat.get("/external_api/sdg/all-sdg-mappings");
        setData(res.data || []);
      } catch (err) {
        const detail =
          err?.response?.data?.detail ||
          err?.message ||
          "Failed to load SDG mappings. Please try again.";
        message.error(detail);
      } finally {
        setLoading(false);
      }
    };

  useEffect(() => {
    fetchDimNames();
    if (mappingType === "indicator") {
      fetchData();
      fetchSdgSeries();
    } else if (mappingType === "country") {
      fetchCountryMappings();
    }
  }, [mappingType]);

  useEffect(() => {
    if (dimensionKeys.length > 0) {
      fetchDimensionOptions();
    }
  }, [dimensionKeys]);


  const fetchCountryMappings = async () => {
      setLoading(true);
      try {
        const res = await AxiosOICStat.get("/external_api/sdg/country-mappings");
        setCountryMappingData(res.data || []);
      } catch (err) {
        const detail =
          err?.response?.data?.detail ||
          err?.message ||
          "Failed to load country mappings. Please try again.";
        message.error(detail);
      } finally {
        setLoading(false);
      }
    };
      

  const filtered = useMemo(() => {
    let filteredData = data.filter((r) => {
      const indCode = r.ind_code;
      return indCode !== null && indCode !== undefined && String(indCode).trim() !== "";
    });

    if (!query.trim()) return filteredData;

    const q = query.toLowerCase();
    return filteredData.filter((r) => {
      const sdesc = String(r.SeriesDescription || "").toLowerCase();
      const scode = String(r.SeriesCode || "").toLowerCase();
      const ind = String(r.ind_code || "");
      return sdesc.includes(q) || scode.includes(q) || ind.includes(q);
    });
  }, [data, query]);

  const filteredCountries = useMemo(() => {
    if (!countryQuery.trim()) return countryMappingData;

    const q = countryQuery.toLowerCase();
    return countryMappingData.filter((r) => {
      const geoCode = String(r.geo_area_code || "").toLowerCase();
      const geoName = String(r.geo_area_name || "").toLowerCase();
      const countryCode = String(r.c_code || "");
      return geoCode.includes(q) || geoName.includes(q) || countryCode.includes(q);
    });
  }, [countryMappingData, countryQuery]);

    const handleDelete = (ind_code) => () => {
        Modal.confirm({
            title: 'Delete SDG Mapping',
            content: `Are you sure you want to delete all SDG mappings for indicator code ${ind_code}? This action cannot be undone.`,
            okText: 'Yes, Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                try {
                    await AxiosOICStat.delete(`/external_api/sdg/sdg-mapping/${ind_code}`);
                    setData((prevData) => prevData.filter((item) => item.ind_code !== ind_code));
                    message.success(`Successfully deleted mapping for indicator code ${ind_code}`);
                } catch (err) {
                    const detail =
                        err?.response?.data?.detail ||
                        err?.message ||
                        `Failed to delete mapping for indicator code ${ind_code}. Please try again.`;
                    message.error(detail);
                }
            },
        });
    };

    const handleAddSubmit = async (values) => {
      try {
        setLoading(true);
        await AxiosOICStat.post(`/external_api/sdg/add-sdg-mapping`, values);
        message.success('SDG mapping added successfully');
        setAddModalVisible(false);
        fetchData();
      } catch (err) {
        const detail =
          err?.response?.data?.detail ||
          err?.message ||
          'Failed to add SDG mapping. Please try again.';
        message.error(detail);
      } finally {
        setLoading(false);
      }
    };

    const handleEditSubmit = async (values) => {
      try {
        setLoading(true);
        await AxiosOICStat.put(`/external_api/sdg/sdg-mapping/${editingRecord.id}`, values);
        
        const clearedDimensions = {};
        Object.values(dimensionFieldMapping).forEach(fieldName => {
          clearedDimensions[fieldName] = null;
        });
        
        setData((prevData) =>
          prevData.map((item) =>
            item.id === editingRecord.id ? { ...item, ...clearedDimensions, ...values } : item
          )
        );

        message.success('SDG mapping updated successfully');
        setEditModalVisible(false);
        setEditingRecord(null);
        editForm.resetFields();
      } catch (err) {
        const detail =
          err?.response?.data?.detail ||
          err?.message ||
          'Failed to update SDG mapping. Please try again.';
        message.error(detail);
      } finally {
        setLoading(false);
      }
    };

    const handleEditCancel = () => {
      setEditModalVisible(false);
      setEditingRecord(null);
      editForm.resetFields();
    };

    const handleCountryEdit = (record) => {
      setEditingCountryRecord(record);
      setCountryEditModalVisible(true);
      countryEditForm.setFieldsValue({
        geo_area_code: record.geo_area_code,
        geo_area_name: record.geo_area_name,
        c_code: record.c_code,
        is_private: record.is_private,
      });
    };

    const handleCountryDelete = (geo_area_code) => {
      Modal.confirm({
        title: 'Delete Country Mapping',
        content: `Are you sure you want to delete the country mapping for geo area code ${geo_area_code}? This action cannot be undone.`,
        okText: 'Yes, Delete',
        okType: 'danger',
        cancelText: 'Cancel',
        onOk: async () => {
          try {
            await AxiosOICStat.delete('/external_api/sdg/country-mappings', {
              data: { geo_area_code }
            });
            setCountryMappingData((prevData) => prevData.filter((item) => item.geo_area_code !== geo_area_code));
            message.success(`Successfully deleted country mapping for geo area code ${geo_area_code}`);
          } catch (err) {
            const detail =
              err?.response?.data?.detail ||
              err?.message ||
              `Failed to delete country mapping for geo area code ${geo_area_code}. Please try again.`;
            message.error(detail);
          }
        },
      });
    };

    const handleCountryAddSubmit = async (values) => {
      try {
        setLoading(true);
        await AxiosOICStat.post('/external_api/sdg/country-mappings', values);
        message.success('Country mapping added successfully');
        setCountryAddModalVisible(false);
        countryAddForm.resetFields();
        fetchCountryMappings();
      } catch (err) {
        const detail =
          err?.response?.data?.detail ||
          err?.message ||
          'Failed to add country mapping. Please try again.';
        message.error(detail);
      } finally {
        setLoading(false);
      }
    };

    const handleCountryEditSubmit = async (values) => {
      try {
        setLoading(true);
        await AxiosOICStat.put('/external_api/sdg/country-mappings', {
          geo_area_code: editingCountryRecord.geo_area_code,
          ...values
        });
        setCountryMappingData((prevData) =>
          prevData.map((item) =>
            item.geo_area_code === editingCountryRecord.geo_area_code ? { ...item, ...values } : item
          )
        );
        message.success('Country mapping updated successfully');
        setCountryEditModalVisible(false);
        setEditingCountryRecord(null);
        countryEditForm.resetFields();
      } catch (err) {
        const detail =
          err?.response?.data?.detail ||
          err?.message ||
          'Failed to update country mapping. Please try again.';
        message.error(detail);
      } finally {
        setLoading(false);
      }
    };

    const handleCountryEditCancel = () => {
      setCountryEditModalVisible(false);
      setEditingCountryRecord(null);
      countryEditForm.resetFields();
    };

  const countryColumns = [
    {
      title: "SDG Geo Area Code",
      dataIndex: "geo_area_code",
      key: "geo_area_code",
      width: 120,
      render: (val) => val ? <Text code>{val}</Text> : <Text type="secondary">—</Text>,
    },
    {
      title: "Geo Area Name",
      dataIndex: "geo_area_name",
      key: "geo_area_name",
      width: 200,
      render: (val) => val ? <Text>{val}</Text> : <Text type="secondary">—</Text>,
    },
    {
      title: "OICStat Country Code",
      dataIndex: "c_code",
      key: "c_code",
      width: 120,
      render: (val) => <Text strong>{val}</Text>,
    },
    {
      title: "Private",
      dataIndex: "is_private",
      key: "is_private",
      width: 80,
      render: (val) => val === 1 ? <Tag color="red">Yes</Tag> : <Tag color="green">No</Tag>,
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Space>
          <Button 
            type="primary" 
            icon={<EditOutlined />} 
            size="small"
            style={{ width: 40 }}
            onClick={() => handleCountryEdit(record)}
          />
          <Button 
            type="primary" 
            danger 
            icon={<DeleteOutlined />} 
            size="small"
            style={{ width: 40 }}
            onClick={() => handleCountryDelete(record.geo_area_code)}
          />
        </Space>
      ),
    },
  ];

  const columns = [
    {
      title: "Series code",
      dataIndex: "SeriesCode",
      key: "SeriesCode",
      width: 180,
      render: (val) => (val ? <Text code>{val}</Text> : "—"),
    },
    {
      title: "Indicator Code",
      dataIndex: "ind_code",
      key: "ind_code",
      width: 140,
      render: (val) => <Text strong>{val}</Text>,
      sorter: (a, b) => (a.ind_code || 0) - (b.ind_code || 0),
    },
    {
      title: "Indicator Name",
      dataIndex: "ind_code",
      key: "indicator_name",
      width: 250,
      render: (indCode) => {
        const indicator = indicatorsState.value?.[indCode];
        return indicator ? (
          <Text style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
            {getIndicatorName(indicator)}
          </Text>
        ) : (
          <Text type="secondary">—</Text>
        );
      },
    },
    {
      title: "Dimensions (non-empty)",
      key: "dimensions",
      render: (_, record) => {
        const nonEmptyDims = dimensionKeys.filter((k) => {
          const fieldName = dimensionFieldMapping[k] || k;
          const value = record?.[fieldName];
          return value !== null && value !== undefined && String(value).trim() !== "";
        });

        if (nonEmptyDims.length === 0) return <Text type="secondary">—</Text>;

        return (
          <Space wrap>
            {nonEmptyDims.map((k) => {
              const fieldName = dimensionFieldMapping[k] || k;
              const value = String(record[fieldName]);
              const label = `${k}: ${value}`;
              const short = value.length > 22 ? `${value.slice(0, 22)}…` : value;
              return (
                <Tooltip title={label} key={`${record.id ?? record.ind_code}-${k}`}>
                  <Tag>{`${k}: ${short}`}</Tag>
                </Tooltip>
              );
            })}
          </Space>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Space>
          <Button 
            type="primary" 
            icon={<EditOutlined />} 
            size="small"
            style={{ width: 40 }}
            onClick={handleEdit(record)}
          />
          <Button 
            type="primary" 
            danger 
            icon={<DeleteOutlined />} 
            size="small"
            style={{ width: 40 }}
            onClick={handleDelete(record.ind_code)}
          />
        </Space>
      ),
    }
  ];

  const indicatorOptions = Object.values(indicatorsState.value || {})
    .filter(indicator => indicator.is_private !== 1)
    .map(indicator => ({
      label: `${indicator.ind_code} - ${getIndicatorName(indicator)}`,
      value: indicator.ind_code
    }));
  
  console.log('countryOptions', countriesState.value);
  const countryOptions = Object.values(countriesState.value || {})
    .map(country => ({
      label: `${country.c_code} - ${country.c_short_name_eng}`,
      value: country.c_code
    }));

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      {mappingType === "indicator" && (
        <Space>
          <Search
            allowClear
            placeholder="Search by Series desc / Series code / ind_code…"
            onSearch={setQuery}
            onChange={(e) => setQuery(e.target.value)}
            style={{ maxWidth: 420 }}
          />
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={() => setAddModalVisible(true)}
          >
            Add New Mapping
          </Button>
        </Space>
      )}

      {mappingType === "country" && (
        <Space>
          <Search
            allowClear
            placeholder="Search by Geo Area Code / Geo Area Name / Country Code…"
            onSearch={setCountryQuery}
            onChange={(e) => setCountryQuery(e.target.value)}
            style={{ maxWidth: 420 }}
          />
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={() => setCountryAddModalVisible(true)}
          >
            Add New Country Mapping
          </Button>
        </Space>
      )}

      <Table
        rowKey={(r) => r.id ?? `${r.SeriesCode}-${r.ind_code}-${r.Year ?? ""}`}
        loading={loading}
        columns={mappingType === "indicator" ? columns : countryColumns}
        dataSource={mappingType === "indicator" ? filtered : filteredCountries}
        size="middle"
        pagination={{ pageSize: 20, showSizeChanger: true }}
        bordered
      />

      {mappingType === "indicator" && (
        <>
          <EditSdgMappingModal
            visible={editModalVisible}
            onCancel={handleEditCancel}
            onUpdate={(values) => {
              setData((prevData) =>
                prevData.map((item) =>
                  item.id === editingRecord.id ? { ...editingRecord, ...values } : item
                )
              );
              setEditModalVisible(false);
              setEditingRecord(null);
              editForm.resetFields();
            }}
            editingRecord={editingRecord}
            editForm={editForm}
            sdgSeries={sdgSeries}
            indicatorOptions={indicatorOptions}
            dimensionKeys={dimensionKeys}
            dimensionOptions={dimensionOptions}
            loadingDimensions={loadingDimensions}
            loading={loading}
            dimensionFieldMapping={dimensionFieldMapping}
          />

          <AddSdgMappingModal
            visible={addModalVisible}
            onSubmit={handleAddSubmit}
            onCancel={() => setAddModalVisible(false)}
            loading={loading}
            indicatorOptions={indicatorOptions}
            dimensionKeys={dimensionKeys}
            dimensionOptions={dimensionOptions}
            loadingDimensions={loadingDimensions}
            sdgSeries={sdgSeries}
          />
        </>
      )}

      {mappingType === "country" && (
        <>
          <Modal
            title="Add Country Mapping"
            open={countryAddModalVisible}
            onOk={() => countryAddForm.submit()}
            onCancel={() => {
              setCountryAddModalVisible(false);
              countryAddForm.resetFields();
            }}
            okText="Add Mapping"
            cancelText="Cancel"
            confirmLoading={loading}
          >
            <Form
              form={countryAddForm}
              layout="vertical"
              onFinish={handleCountryAddSubmit}
            >
              <Form.Item
                name="geo_area_code"
                label="SDG Geo Area Code"
                rules={[{ required: true, message: 'Please enter geo area code' }]}
              >
                <Input placeholder="e.g., 792" />
              </Form.Item>

              <Form.Item
                name="geo_area_name"
                label="Geo Area Name"
                rules={[{ required: true, message: 'Please enter geo area name' }]}
              >
                <Input placeholder="e.g., Türkiye" />
              </Form.Item>

              <Form.Item
                name="c_code"
                label="OICStat Country Code"
                rules={[{ required: true, message: 'Please select country code' }]}
              >
                <Select
                  showSearch
                  autoClearSearchValue={false}
                  placeholder="Select Country"
                  options={countryOptions}
                  filterOption={(input, option) =>
                    option.label.toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Form.Item>

              <Form.Item
                name="is_private"
                label="Private"
                initialValue={0}
              >
                <Select>
                  <Option value={0}>No</Option>
                  <Option value={1}>Yes</Option>
                  </Select>
              </Form.Item>
            </Form>
          </Modal>

          <EditCountryMappingModal
            visible={countryEditModalVisible}
            onCancel={handleCountryEditCancel}
            onUpdate={(values) => {
              setCountryMappingData((prevData) =>
                prevData.map((item) =>
                  item.geo_area_code === editingCountryRecord.geo_area_code ? { ...item, ...values } : item
                )
              );
              setCountryEditModalVisible(false);
              setEditingCountryRecord(null);
              countryEditForm.resetFields();
            }}
            editingRecord={editingCountryRecord}
            editForm={countryEditForm}
            loading={loading}
            countryOptions={countryOptions}
          />
        </>
      )}
    </Space>
  );
}